<template>
  <div class="builder-canvas">
    <div 
      class="canvas-viewport" 
      :class="responsiveMode"
      @drop="onDrop"
      @dragover.prevent
    >
      <!-- Empty state -->
      <div v-if="!structure.rows || structure.rows.length === 0" class="empty-state">
        <div class="empty-state-icon">📐</div>
        <div class="empty-state-title">Start Building</div>
        <div class="empty-state-text">
          Drag elements from the left panel or click the button below
        </div>
        <button class="btn btn-primary" @click="addRow">
          ➕ Add Row
        </button>
      </div>

      <!-- Rows -->
      <div 
        v-for="(row, rowIndex) in structure.rows" 
        :key="row.id"
        :class="['canvas-row', { 'selected': isSelected(row) }]"
        @click.stop="selectElement(row)"
      >
        <!-- Row toolbar -->
        <div class="element-toolbar">
          <button @click.stop="addColumn(rowIndex)" title="Add Column">
            ➕
          </button>
          <button @click.stop="duplicateRow(rowIndex)" title="Duplicate Row">
            📋
          </button>
          <button @click.stop="deleteRow(rowIndex)" title="Delete Row">
            🗑️
          </button>
        </div>

        <div class="canvas-row-inner">
          <!-- Columns -->
          <div 
            v-for="(column, colIndex) in row.columns" 
            :key="column.id"
            :class="['canvas-column', { 'selected': isSelected(column) }]"
            :style="{ flex: `0 0 ${(column.width / 12) * 100}%`, maxWidth: `${(column.width / 12) * 100}%` }"
            @click.stop="selectElement(column)"
            @drop="onDropInColumn($event, rowIndex, colIndex)"
            @dragover.prevent
          >
            <!-- Column toolbar -->
            <div class="element-toolbar">
              <button @click.stop="changeColumnWidth(rowIndex, colIndex, -1)" title="Decrease Width">
                ◀️
              </button>
              <span class="column-width-indicator">{{ column.width }}/12</span>
              <button @click.stop="changeColumnWidth(rowIndex, colIndex, 1)" title="Increase Width">
                ▶️
              </button>
              <button @click.stop="deleteColumn(rowIndex, colIndex)" title="Delete Column">
                🗑️
              </button>
            </div>

            <!-- Elements -->
            <div 
              v-for="(element, elIndex) in column.elements || []" 
              :key="element.id"
              :class="['canvas-element', { 'selected': isSelected(element) }]"
              @click.stop="selectElement(element)"
              draggable="true"
              @dragstart="onElementDragStart($event, rowIndex, colIndex, elIndex)"
            >
              <!-- Element toolbar -->
              <div class="element-toolbar">
                <button @click.stop="moveElementUp(rowIndex, colIndex, elIndex)" 
                        :disabled="elIndex === 0"
                        title="Move Up">
                  ⬆️
                </button>
                <button @click.stop="moveElementDown(rowIndex, colIndex, elIndex)" 
                        :disabled="elIndex === (column.elements?.length || 0) - 1"
                        title="Move Down">
                  ⬇️
                </button>
                <button @click.stop="duplicateElement(rowIndex, colIndex, elIndex)" title="Duplicate">
                  📋
                </button>
                <button @click.stop="deleteElement(rowIndex, colIndex, elIndex)" title="Delete">
                  🗑️
                </button>
              </div>

              <!-- Element content preview -->
              <div class="element-preview" v-html="renderElementPreview(element)"></div>
            </div>

            <!-- Drop zone indicator -->
            <div v-if="(column.elements || []).length === 0" class="drop-zone-placeholder">
              Drop elements here
            </div>
          </div>
        </div>
      </div>

      <!-- Add row button -->
      <div v-if="structure.rows && structure.rows.length > 0" class="canvas-add-row">
        <button class="btn btn-outline" @click="addRow">
          ➕ Add Row
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';

const props = defineProps({
  structure: {
    type: Object,
    required: true
  },
  responsiveMode: {
    type: String,
    default: 'desktop'
  },
  selectedElement: {
    type: Object,
    default: null
  }
});

const emit = defineEmits(['select', 'update', 'delete']);

// Check if element is selected
function isSelected(element) {
  return props.selectedElement && props.selectedElement.id === element.id;
}

// Select element
function selectElement(element) {
  emit('select', element);
}

// Add row
function addRow() {
  const newRow = {
    id: 'row_' + Date.now(),
    columns: [
      {
        id: 'col_' + Date.now(),
        width: 12,
        elements: [],
        styles: {}
      }
    ],
    styles: {}
  };

  const updated = { ...props.structure };
  if (!updated.rows) updated.rows = [];
  updated.rows.push(newRow);
  emit('update', updated);
}

// Add column to row
function addColumn(rowIndex) {
  const updated = { ...props.structure };
  const row = updated.rows[rowIndex];
  
  // Calculate width for new column
  const totalWidth = row.columns.reduce((sum, col) => sum + col.width, 0);
  const newWidth = Math.min(6, 12 - totalWidth);
  
  if (newWidth <= 0) {
    alert('Row is full. Maximum 12 columns total.');
    return;
  }

  const newColumn = {
    id: 'col_' + Date.now(),
    width: newWidth,
    elements: [],
    styles: {}
  };

  row.columns.push(newColumn);
  emit('update', updated);
}

// Delete row
function deleteRow(rowIndex) {
  if (!confirm('Delete this row and all its contents?')) return;
  
  const updated = { ...props.structure };
  updated.rows.splice(rowIndex, 1);
  emit('update', updated);
  emit('select', null);
}

// Duplicate row
function duplicateRow(rowIndex) {
  const updated = { ...props.structure };
  const original = updated.rows[rowIndex];
  const duplicate = JSON.parse(JSON.stringify(original));
  
  // Generate new IDs
  duplicate.id = 'row_' + Date.now();
  duplicate.columns.forEach((col, i) => {
    col.id = 'col_' + Date.now() + '_' + i;
    col.elements?.forEach((el, j) => {
      el.id = 'el_' + Date.now() + '_' + i + '_' + j;
    });
  });
  
  updated.rows.splice(rowIndex + 1, 0, duplicate);
  emit('update', updated);
}

// Delete column
function deleteColumn(rowIndex, colIndex) {
  if (!confirm('Delete this column and all its elements?')) return;
  
  const updated = { ...props.structure };
  updated.rows[rowIndex].columns.splice(colIndex, 1);
  
  // If no columns left, delete row
  if (updated.rows[rowIndex].columns.length === 0) {
    updated.rows.splice(rowIndex, 1);
  }
  
  emit('update', updated);
  emit('select', null);
}

// Change column width
function changeColumnWidth(rowIndex, colIndex, delta) {
  const updated = { ...props.structure };
  const column = updated.rows[rowIndex].columns[colIndex];
  const newWidth = column.width + delta;
  
  if (newWidth < 1 || newWidth > 12) return;
  
  // Check total width
  const otherColumns = updated.rows[rowIndex].columns.filter((_, i) => i !== colIndex);
  const otherTotalWidth = otherColumns.reduce((sum, col) => sum + col.width, 0);
  
  if (otherTotalWidth + newWidth > 12) {
    alert('Total column width cannot exceed 12');
    return;
  }
  
  column.width = newWidth;
  emit('update', updated);
}

// Drop handler for new elements
function onDrop(event) {
  event.preventDefault();
  const blockType = event.dataTransfer.getData('block-type');
  
  if (blockType && (!props.structure.rows || props.structure.rows.length === 0)) {
    // If no rows, create one first
    addRow();
  }
}

// Drop handler for column
function onDropInColumn(event, rowIndex, colIndex) {
  event.preventDefault();
  event.stopPropagation();
  
  const blockType = event.dataTransfer.getData('block-type');
  if (!blockType) return;
  
  const newElement = createDefaultElement(blockType);
  
  const updated = { ...props.structure };
  const column = updated.rows[rowIndex].columns[colIndex];
  
  if (!column.elements) column.elements = [];
  column.elements.push(newElement);
  
  emit('update', updated);
  emit('select', newElement);
}

// Element drag start
function onElementDragStart(event, rowIndex, colIndex, elIndex) {
  event.dataTransfer.effectAllowed = 'move';
  event.dataTransfer.setData('element-move', JSON.stringify({
    rowIndex,
    colIndex,
    elIndex
  }));
}

// Delete element
function deleteElement(rowIndex, colIndex, elIndex) {
  if (!confirm('Delete this element?')) return;
  
  const updated = { ...props.structure };
  updated.rows[rowIndex].columns[colIndex].elements.splice(elIndex, 1);
  
  emit('update', updated);
  emit('select', null);
}

// Duplicate element
function duplicateElement(rowIndex, colIndex, elIndex) {
  const updated = { ...props.structure };
  const original = updated.rows[rowIndex].columns[colIndex].elements[elIndex];
  const duplicate = JSON.parse(JSON.stringify(original));
  
  duplicate.id = 'el_' + Date.now();
  
  updated.rows[rowIndex].columns[colIndex].elements.splice(elIndex + 1, 0, duplicate);
  emit('update', updated);
}

// Move element up
function moveElementUp(rowIndex, colIndex, elIndex) {
  if (elIndex === 0) return;
  
  const updated = { ...props.structure };
  const elements = updated.rows[rowIndex].columns[colIndex].elements;
  
  [elements[elIndex], elements[elIndex - 1]] = [elements[elIndex - 1], elements[elIndex]];
  
  emit('update', updated);
}

// Move element down
function moveElementDown(rowIndex, colIndex, elIndex) {
  const updated = { ...props.structure };
  const elements = updated.rows[rowIndex].columns[colIndex].elements;
  
  if (elIndex === elements.length - 1) return;
  
  [elements[elIndex], elements[elIndex + 1]] = [elements[elIndex + 1], elements[elIndex]];
  
  emit('update', updated);
}

// Create default element
function createDefaultElement(type) {
  const id = 'el_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
  
  const defaults = {
    text: { content: 'Your text here...', tag: 'p', align: 'left', class: '' },
    image: { src: '', alt: 'Image', width: '100%', link: '', class: '' },
    links: { items: [{ text: 'Link 1', url: '#' }], layout: 'vertical', class: '' },
    social: { networks: [{ name: 'facebook', url: 'https://facebook.com' }], style: 'default', class: '' },
    menu: { menu_slug: 'main-menu', class: '' },
    newsletter: { placeholder: 'Enter your email', button_text: 'Subscribe', action_url: '/newsletter/subscribe', class: '' },
    html: { content: '<div>Custom HTML</div>', class: '' },
    divider: { height: '1px', style: 'solid', color: '#e5e7eb', class: '' },
    spacer: { height: '20px' }
  };

  return {
    id,
    type,
    settings: defaults[type] || {}
  };
}

// Render element preview
function renderElementPreview(element) {
  const settings = element.settings || {};
  
  switch (element.type) {
    case 'text':
      return `<${settings.tag || 'p'} style="text-align:${settings.align || 'left'}">${settings.content || 'Text'}</${settings.tag || 'p'}>`;
    
    case 'image':
      return settings.src 
        ? `<img src="${settings.src}" alt="${settings.alt || ''}" style="max-width:${settings.width || '100%'};height:auto" />`
        : '<div style="padding:40px;background:#f3f4f6;text-align:center;color:#9ca3af">🖼️ No image selected</div>';
    
    case 'links':
      const links = (settings.items || []).map(item => 
        `<li><a href="${item.url || '#'}">${item.text || 'Link'}</a></li>`
      ).join('');
      return `<ul style="list-style:none;padding:0">${links}</ul>`;
    
    case 'social':
      return '<div style="display:flex;gap:10px">👥 Social Icons</div>';
    
    case 'menu':
      return '<div style="padding:10px;background:#f3f4f6">📋 Menu: ' + (settings.menu_slug || 'main-menu') + '</div>';
    
    case 'newsletter':
      return '<div style="padding:10px;background:#f3f4f6">📧 Newsletter Form</div>';
    
    case 'html':
      return settings.content || '<div>Custom HTML</div>';
    
    case 'divider':
      return `<hr style="border:none;border-top:${settings.height || '1px'} ${settings.style || 'solid'} ${settings.color || '#e5e7eb'}" />`;
    
    case 'spacer':
      return `<div style="height:${settings.height || '20px'}"></div>`;
    
    default:
      return '<div>Unknown element</div>';
  }
}
</script>

<style scoped>
.canvas-add-row {
  text-align: center;
  padding: 20px;
  border-top: 2px dashed #e5e7eb;
  margin-top: 20px;
}

.drop-zone-placeholder {
  padding: 40px;
  border: 2px dashed #d1d5db;
  border-radius: 8px;
  text-align: center;
  color: #9ca3af;
  background: #f9fafb;
  min-height: 100px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.column-width-indicator {
  font-size: 11px;
  padding: 0 8px;
  color: #6b7280;
  font-weight: 600;
}

.element-preview {
  pointer-events: none;
  user-select: none;
}

.element-preview img {
  max-width: 100%;
  height: auto;
}
</style>

